// Web Push + service-worker helpers — Phase 3 Module 8.
//
// Why this file exists:
//   * Registers our single service worker at /service-worker.js.
//   * Owns the full subscribe / unsubscribe lifecycle (permission →
//     pushManager → Supabase RPC upsert).
//   * Returns a uniform `PushState` object so the UI can render a tiny
//     finite-state-machine of: unsupported / default / denied / granted+off
//     / granted+on. Nothing else in the app needs to know about ServiceWorker
//     registrations or Notification APIs.
//   * All DB writes go through the SECURITY DEFINER RPCs in migration
//     20260801000000_push_notifications.sql.

import axios from "axios";
import { supabase, isSupabaseConfigured } from "./supabase";

const API = process.env.REACT_APP_BACKEND_URL;
const SW_URL = "/service-worker.js";

// ---------------------------------------------------------------------------
// Feature detection
// ---------------------------------------------------------------------------
export function isPushSupported() {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

// ---------------------------------------------------------------------------
// Service worker registration (idempotent)
// ---------------------------------------------------------------------------
let _swReadyPromise = null;

export function registerServiceWorker() {
  if (!isPushSupported()) return Promise.resolve(null);
  if (_swReadyPromise) return _swReadyPromise;
  _swReadyPromise = (async () => {
    try {
      // Only (re)register if not already controlling this page. CRA's dev
      // server and hot reload play nicely with this approach.
      const existing = await navigator.serviceWorker.getRegistration(SW_URL);
      const reg =
        existing || (await navigator.serviceWorker.register(SW_URL, { scope: "/" }));
      await navigator.serviceWorker.ready;
      return reg;
    } catch (err) {
      // Never crash the app on a service-worker registration failure —
      // the rest of the UI must keep working in browsers that block SWs.
      // eslint-disable-next-line no-console
      console.warn("push: service worker registration failed", err);
      _swReadyPromise = null;
      return null;
    }
  })();
  return _swReadyPromise;
}

// Listen for notificationclick deep-links that couldn't be navigated by
// the SW (iOS Safari blocks client.navigate across origins). The SW posts
// `{type:'navigate', url}` and we route via the SPA.
export function wireNotificationClickRouting(navigate) {
  if (!isPushSupported() || !navigator.serviceWorker) return () => {};
  const handler = (ev) => {
    const msg = ev.data || {};
    if (msg && msg.type === "navigate" && typeof msg.url === "string") {
      try {
        navigate(msg.url);
      } catch {
        window.location.assign(msg.url);
      }
    }
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () =>
    navigator.serviceWorker.removeEventListener("message", handler);
}

// ---------------------------------------------------------------------------
// VAPID key fetch + application-server-key conversion
// ---------------------------------------------------------------------------
let _publicKeyCache = null;
export async function fetchVapidPublicKey() {
  if (_publicKeyCache !== null) return _publicKeyCache;
  try {
    const res = await axios.get(`${API}/api/push/public-key`);
    _publicKeyCache = res.data?.publicKey || null;
  } catch {
    _publicKeyCache = null;
  }
  return _publicKeyCache;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function subToJson(sub) {
  const raw = sub.toJSON();
  // raw = { endpoint, keys: { p256dh, auth }, expirationTime }
  return {
    endpoint: raw.endpoint,
    p256dh:   raw.keys?.p256dh || "",
    auth:     raw.keys?.auth   || "",
  };
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------
export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await registerServiceWorker();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Returns the full state the UI needs to render the preference control:
 *   supported     — boolean
 *   permission    — 'default' | 'granted' | 'denied' | 'unsupported'
 *   subscribed    — true iff a browser subscription exists
 *   endpoint      — current endpoint (debug only)
 */
export async function getPushState() {
  const supported = isPushSupported();
  if (!supported) {
    return { supported: false, permission: "unsupported", subscribed: false, endpoint: null };
  }
  const permission = getNotificationPermission();
  const sub = await getCurrentSubscription();
  return {
    supported: true,
    permission,
    subscribed: !!sub,
    endpoint: sub ? sub.endpoint : null,
  };
}

// ---------------------------------------------------------------------------
// Subscribe / unsubscribe
// ---------------------------------------------------------------------------

/**
 * Interactive enable flow. Must be called from a user gesture (button
 * click) — Notification.requestPermission() requires it on all browsers.
 * Returns the resolved PushState.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error("Push notifications are not supported on this device");
  }
  if (!isSupabaseConfigured) {
    throw new Error("Push requires live Supabase");
  }

  // Permission must be 'granted' before pushManager.subscribe().
  if (Notification.permission === "default") {
    const result = await Notification.requestPermission();
    if (result !== "granted") {
      const err = new Error(
        result === "denied"
          ? "Notifications are blocked in your browser settings"
          : "Permission was not granted",
      );
      err.code = result;
      throw err;
    }
  } else if (Notification.permission === "denied") {
    const err = new Error("Notifications are blocked in your browser settings");
    err.code = "denied";
    throw err;
  }

  const publicKey = await fetchVapidPublicKey();
  if (!publicKey) {
    throw new Error("Push is not configured on the server yet");
  }

  const reg = await registerServiceWorker();
  if (!reg) throw new Error("Service worker failed to register");

  // Re-use an existing subscription if one is already registered for
  // this SW+applicationServerKey (prevents churn on reloads).
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const { endpoint, p256dh, auth } = subToJson(sub);
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;

  const { error } = await supabase.rpc("rpc_upsert_push_subscription", {
    p_endpoint:   endpoint,
    p_p256dh:     p256dh,
    p_auth:       auth,
    p_user_agent: userAgent,
  });
  if (error) {
    // Clean up the browser-side subscription so we don't leave an
    // orphaned token that the server has no key material for.
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore */
    }
    throw new Error(error.message || "Could not save push subscription");
  }

  return getPushState();
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return getPushState();
  const sub = await getCurrentSubscription();
  if (!sub) return getPushState();

  const { endpoint } = subToJson(sub);
  // Tell the server first — if it fails we still want to try the browser
  // unsubscribe so the device doesn't keep receiving pushes.
  try {
    await supabase.rpc("rpc_delete_push_subscription", { p_endpoint: endpoint });
  } catch {
    /* non-fatal: the SW unsubscribe below still shuts delivery down */
  }
  try {
    await sub.unsubscribe();
  } catch {
    /* ignore */
  }
  return getPushState();
}
